// Helpers to normalize and hydrate free sample selections from cart attributes
function normalizeFreeSampleSelection(entry, products = []) {
  const variantId = parseInt(
    typeof entry === 'number' ? entry : entry?.variantId || entry?.id,
    10
  );
  const quantityValue = typeof entry === 'number' ? 1 : parseInt(entry?.quantity || entry?.qty || 1, 10);
  const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;

  const matchById = products.find(product => product.variant_id === variantId || product.id === variantId);
  const matchBySku = entry?.sku
    ? products.find(product => product.variant_sku === entry.sku)
    : null;
  const title = (entry?.title || '').toLowerCase();
  const matchByTitle = title
    ? products.find(product => (product.title || '').toLowerCase() === title)
    : null;

  const matchedProduct = matchById || matchBySku || matchByTitle || {};
  const resolvedVariantId = variantId || matchedProduct.variant_id || matchedProduct.id;

  if (!resolvedVariantId) {
    return null;
  }

  return {
    variantId: resolvedVariantId,
    productId: entry?.productId || matchedProduct.id || null,
    title: entry?.title || matchedProduct.title || 'Free sample',
    sku: entry?.sku || matchedProduct.variant_sku || '',
    image: entry?.image || matchedProduct.image || '',
    quantity
  };
}

function hydrateSamplesFromReadableAttributes(attributes = {}, products = []) {
  const samples = [];

  [1, 2].forEach((slot) => {
    const name = attributes[`sample_${slot}_name`];
    const sku = attributes[`sample_${slot}_sku`];
    const image = attributes[`sample_${slot}_image`];

    if (!name && !sku && !image) {
      return;
    }

    const matchedProduct = products.find((product) => {
      const matchesSku = sku && product.variant_sku === sku;
      const matchesTitle = name && (product.title || '').toLowerCase() === name.toLowerCase();
      return matchesSku || matchesTitle;
    }) || {};

    const entry = normalizeFreeSampleSelection({
      variantId: matchedProduct.variant_id || matchedProduct.id || null,
      productId: matchedProduct.id || null,
      title: name || matchedProduct.title || 'Free sample',
      sku: sku || matchedProduct.variant_sku || '',
      image: image || matchedProduct.image || '',
      quantity: 1
    }, products);

    if (entry) {
      samples.push(entry);
    }
  });

  return samples;
}

function limitFreeSamplesToTwo(selections = []) {
  const result = [];
  let remaining = 2;

  selections.forEach((selection) => {
    if (remaining <= 0) {
      return;
    }
    const qty = Math.min(selection.quantity || 1, remaining);
    if (qty > 0) {
      result.push({ ...selection, quantity: qty });
      remaining -= qty;
    }
  });

  return result;
}

function parseFreeSampleAttribute(attributes = {}, products = []) {
  const rawSamples = attributes.free_samples;

  if (rawSamples) {
    try {
      const parsed = typeof rawSamples === 'string' ? JSON.parse(rawSamples) : rawSamples;
      if (Array.isArray(parsed)) {
        const normalized = parsed
          .map((entry) => normalizeFreeSampleSelection(entry, products))
          .filter(Boolean);

        if (normalized.length) {
          return limitFreeSamplesToTwo(normalized);
        }
      }
    } catch (error) {
      console.error('Error parsing free sample attributes:', error);
    }
  }

  return limitFreeSamplesToTwo(hydrateSamplesFromReadableAttributes(attributes, products));
}

document.addEventListener('alpine:init', () => {
  Alpine.store('cart', {
    cart: window.sideCart.cart,
    items: []
  });

  Alpine.data('sidecart', () => ({
    isOpen: false,
    loading: false,
    cartSellingPlans: {},
    freeGiftThreshold: window.sideCart.settings.freeGiftThreshold,
    freeGiftVariantId: window.sideCart.settings.freeGiftVariantId,
    freeGiftVariants: window.sideCart.settings.freeGiftVariants || [],
    freeGiftSyncInProgress: false,
    
    init() {
      this.fetchSellingPlans();

      if (PUB_SUB_EVENTS) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (eventData) => {
          this.open();
        });
      }

      // Listen for cart updates
      window.addEventListener('cart:updated', (event) => {
          this.$store.cart.cart = event.detail.cart;
          this.$store.cart.items = event.detail.cart.items;
          this.fetchSellingPlans();
          this.syncFreeGift();
      });

      window.addEventListener('cart:refresh', (event) => {
        this.fetchCart();
      });

      window.addEventListener('cart:add-free-sample', (event) => {
        const variantId = event.detail?.variantId;
        if (variantId) {
          this.addFreeSampleSelection(parseInt(variantId, 10));
        }
      });
      
      
      // Listen for cart open events
      window.addEventListener('cart:open', () => {
        this.open();
      });
      
      // Add click listener for cart icon in header
      this.setupCartIconListener();
    },

    get cartItems(){
      let items = this.$store.cart.cart.items ? [...this.$store.cart.cart.items] : [];

      if (items.length) {
        items.forEach(item => {
          if (typeof item.final_price !== 'number') {
            item.final_price = typeof item.discounted_price === 'number' ? item.discounted_price : item.price;
          }

          const sellingPlans = this.cartSellingPlans[item.key];
          if (sellingPlans) {
            item.selling_plans = sellingPlans;
          }
        });
      }

      return items.concat(this.freeSampleVirtualItems);
    },

    get freeSampleSelections(){
      const products = window.freeSampleProducts || [];
      const attributes = this.$store.cart.cart?.attributes || {};
      return parseFreeSampleAttribute(attributes, products);
    },

    get freeSampleVirtualItems(){
      const products = window.freeSampleProducts || [];

      return this.freeSampleSelections.map((selection, index) => {
        const matchedProduct = products.find(product => product.variant_id === selection.variantId || product.id === selection.variantId);

        return {
          key: `free-sample-${selection.variantId}-${index}`,
          product_title: selection.title || matchedProduct?.title || 'Free sample',
          variant_title: matchedProduct?.variant_title || '',
          image: selection.image || matchedProduct?.image || '',
          url: matchedProduct?.url || '#',
          price: 0,
          final_price: 0,
          discounted_price: 0,
          quantity: selection.quantity || 1,
          isFreeSample: true,
          variant_id: selection.variantId,
          sku: selection.sku || matchedProduct?.variant_sku || '',
          item_components: []
        };
      });
    },

    get totalSamplesInCart(){
      return this.freeSampleSelections.reduce((total, selection) => total + (selection.quantity || 1), 0);
    },

    get remainingToFreeGift(){
      return this.formatMoney(this.freeGiftThreshold - this.$store.cart.cart.total_price);
    },

    get isFreeGiftEligible(){
      const subtotal = this.$store.cart.cart.items
      .filter(item => item.properties?._widget !== "cart9202689gqp")
      .reduce((total, item) => total + item.final_line_price, 0);
      
      return subtotal >= this.freeGiftThreshold;
    },

    get isFreeGiftInCart(){
      const result = this.$store.cart.cart.items.some((item) => {
        return item.properties && item.properties?._widget === 'cart9202689gqp';
      });

      return result;
    },

    get isFreeGiftManuallyRemoved(){
      return this.$store.cart.cart.attributes && 
             this.$store.cart.cart.attributes.free_gift_removed_v2 === 'true';
    },

    async addFreeSampleSelection(variantId) {
      const normalizedId = parseInt(variantId, 10);
      if (!normalizedId) {
        return;
      }

      const selections = [...this.freeSampleSelections];
      const totalQuantity = selections.reduce((sum, entry) => sum + (entry.quantity || 1), 0);
      if (totalQuantity >= 2) {
        return;
      }

      const productData = (window.freeSampleProducts || []).find(product => product.variant_id === normalizedId || product.id === normalizedId) || {};
      const existingIndex = selections.findIndex((selection) => selection.variantId === normalizedId);

      if (existingIndex > -1) {
        selections[existingIndex].quantity = Math.min(2, selections[existingIndex].quantity + 1);
      } else {
        const newSelection = {
          variantId: normalizedId,
          productId: productData.id || null,
          title: productData.title || 'Free sample',
          sku: productData.variant_sku || '',
          image: productData.image || '',
          quantity: 1
        };
        selections.push(newSelection);
      }

      this.loading = true;

      try {
        await this.syncFreeSampleAttributes(selections);
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: this.$store.cart.cart } }));
      } finally {
        this.loading = false;
      }
    },

    async removeFreeSampleSelection(variantId) {
      await this.updateFreeSampleQuantity(variantId, 0);
    },

    // New method to select a specific free gift
    async selectFreeGift(variantId) {
      // First, remove any existing free gift
      const existingFreeGift = this.$store.cart.cart.items.find(item => 
        item.properties && item.properties._widget === 'cart9202689gqp'
      );
      
      if (existingFreeGift) {
        await this.removeItem(existingFreeGift.key);
      }
      
      // Add the selected free gift
      await this.addToCart(variantId, {'_widget': 'cart9202689gqp'});
    },

    captureSellingPlans(){
      const jsonScript = document.querySelector('#cart-selling-plans-data');
      const data = JSON.parse(jsonScript.textContent);
      this.cartSellingPlans = data;
    },
    
    setupCartIconListener() {
      // Wait for DOM to be fully loaded
      document.addEventListener('DOMContentLoaded', () => {
        const cartIcons = document.querySelectorAll('.header__icon--cart, .header__icon.header__icon--cart');
        
        cartIcons.forEach(icon => {
          icon.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.open();
          });
        });
      });
    },
    
    open() {
      document.body.classList.add('rh-sidecart-open');
      this.fetchCart();
      // Set isOpen after a tiny delay to ensure CSS transitions work properly
      setTimeout(() => {
        this.isOpen = true;
      }, 10);
    },
    
    close() {
      this.isOpen = false;
      setTimeout(() => {
        document.body.classList.remove('rh-sidecart-open');
      }, 300); // Match the transition duration
    },
    
    async fetchCart() {
      this.loading = true;
      try {
        // Use the cart-data section to get full cart data with properties
        const response = await fetch('/?section_id=cart-data');
        const html = await response.text();
        
        // Parse the HTML to extract cart data
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        const cartDataScript = tempDiv.querySelector('#cart-data');
        if (cartDataScript) {
          const cartData = JSON.parse(cartDataScript.textContent);
         
          this.$store.cart.cart = cartData;
          this.fetchSellingPlans();
          await this.syncFreeGift();
        } else {
          // Fallback to cart.js if section method fails
          const fallbackResponse = await fetch('/cart.js');
          const fallbackData = await fallbackResponse.json();
          this.$store.cart.cart = fallbackData;
          this.fetchSellingPlans();
          await this.syncFreeGift();
        }
        
      } catch (error) {
        // Final fallback to simple cart.js
        try {
          const response = await fetch('/cart.js');
          const data = await response.json();
          this.$store.cart.cart = data;
          this.fetchSellingPlans();
          await this.syncFreeGift();
        } catch (fallbackError) {
          console.error('Error with fallback cart fetch:', fallbackError);
        }
      } finally {
        this.loading = false;
      }
    },
    
    async updateItemQuantity(key, quantity) {
      quantity = parseInt(quantity);
      
      // Show immediate feedback by updating the local data first
      const itemIndex = this.$store.cart.items.findIndex(item => item.key === key);
      if (itemIndex !== -1) {
        if (quantity <= 0) {
          // Visually remove item from cart immediately
          this.$store.cart.items.splice(itemIndex, 1);
          if (this.$store.cart.items.length === 0) {
            this.$store.cart.item_count = 0;
          }
        } else {
          // Update the item quantity locally
          const oldQuantity = this.$store.cart.items[itemIndex].quantity;
          this.$store.cart.items[itemIndex].quantity = quantity;
          this.$store.cart.item_count = this.$store.cart.item_count - oldQuantity + quantity;
        }
      }
      
      // Then send the update to the server
      try {
        this.loading = true;
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        
        // Update with the server response to ensure accuracy
        //this.$store.cart.cart = await response.json();
        //this.loading = false;

        this.fetchCart();

        //this.checkIfFreeGiftShouldBeRemoved();
        //window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: this.$store.cart.cart } }));
      } catch (error) {
        console.error('Error updating item quantity:', error);
        // If there was an error, refresh the cart to ensure data is correct
        this.fetchCart();
      }
    },
    
    async removeItem(key) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        
        this.fetchCart();

        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: this.$store.cart.cart } }));
      } catch (error) {
        console.error('Error removing item:', error);
      }

      return true;
    },
    
    upgradeToSubscription(itemKey, selectedPlanId, quantity) {
      this.loading = true;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: itemKey,
          selling_plan: parseInt(selectedPlanId),
          quantity: quantity
        })
      })
      .then(res => res.json())
      .then(() => {
        this.fetchCart();
      })
      .finally(() =>{
        this.loading = false;
      })
    },
    
    formatMoney(cents) {
      return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: window.sideCart.currencyIsoCode
      });
    },
    
    fetchSellingPlans() {
      fetch('/?section_id=cart-selling-plans')
        .then(res => res.text())
        .then(html => {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const jsonScript = tempDiv.querySelector('#cart-selling-plans-data');
          if (jsonScript) {
            this.cartSellingPlans = JSON.parse(jsonScript.textContent);
          }
        });
    },

    addFreeGiftToCart(variantId){
      // Only used for backwards compatibility or manual selection
      if (variantId) {
        this.addToCart(variantId, {'_widget': 'cart9202689gqp'});
      }
    },

    async syncFreeGift(){
      if (this.freeGiftSyncInProgress) return;

      // Reset free gift removal flag if cart is empty
      if (this.$store.cart.cart.item_count === 0 && this.isFreeGiftManuallyRemoved) {
        await this.updateCartAttributes({ free_gift_removed_v2: null });
        return;
      }

      if (!this.isFreeGiftEligible && this.isFreeGiftInCart) {
        // If the free gift is in the cart but not eligible, remove it and clear the manual removal flag
        const freeGiftItem = this.$store.cart.cart.items.find(item => 
          item.properties && 
          item.properties._widget === 'cart9202689gqp'
        );
        
        await this.removeItem(freeGiftItem.key);
        // Clear the manual removal flag when automatically removing due to threshold
        await this.updateCartAttributes({ free_gift_removed_v2: null });

        return;
      }

      if (
        this.isFreeGiftEligible &&
        !this.isFreeGiftInCart &&
        !this.isFreeGiftManuallyRemoved &&
        this.freeGiftVariantId
      ) {
        this.freeGiftSyncInProgress = true;

        try {
          await this.addToCart(this.freeGiftVariantId, {'_widget': 'cart9202689gqp'});
        } finally {
          this.freeGiftSyncInProgress = false;
        }
      }
    },

    async removeFreeGiftManually() {
      const freeGiftItem = this.$store.cart.cart.items.find(item => 
        item.properties && 
        item.properties._widget === 'cart9202689gqp'
      );

      if (freeGiftItem) {
        // Set cart attribute to prevent auto-adding
        await this.updateCartAttributes({ free_gift_removed_v2: 'true' });
        // Remove the item
        await this.removeItem(freeGiftItem.key);
      }
    },

    async addFreeGiftBack() {
      // Clear the manual removal flag  
      await this.updateCartAttributes({ free_gift_removed_v2: null });
      // Note: User will need to select a gift manually, we don't auto-add anymore
    },

    async updateFreeSampleQuantity(variantId, desiredQuantity) {
      const normalizedId = parseInt(variantId, 10);
      if (!normalizedId && normalizedId !== 0) {
        return;
      }

      const newQuantityValue = parseInt(desiredQuantity, 10);
      const newQuantity = Number.isFinite(newQuantityValue) && newQuantityValue > 0 ? newQuantityValue : 0;

      const selections = [...this.freeSampleSelections];
      const existingIndex = selections.findIndex((selection) => selection.variantId === normalizedId);
      const otherTotal = selections.reduce((sum, selection, index) => {
        if (index === existingIndex) {
          return sum;
        }
        return sum + (selection.quantity || 1);
      }, 0);
      const maxAllowedForThis = Math.max(0, 2 - otherTotal);
      const clampedQuantity = Math.min(newQuantity, maxAllowedForThis);

      if (clampedQuantity === 0) {
        if (existingIndex > -1) {
          selections.splice(existingIndex, 1);
        } else {
          return;
        }
      } else if (existingIndex > -1) {
        selections[existingIndex].quantity = clampedQuantity;
      } else {
        const productData = (window.freeSampleProducts || []).find(product => product.variant_id === normalizedId || product.id === normalizedId) || {};
        selections.push({
          variantId: normalizedId,
          productId: productData.id || null,
          title: productData.title || 'Free sample',
          sku: productData.variant_sku || '',
          image: productData.image || '',
          quantity: clampedQuantity
        });
      }

      this.loading = true;

      try {
        await this.syncFreeSampleAttributes(selections);
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: this.$store.cart.cart } }));
      } finally {
        this.loading = false;
      }
    },

    buildSampleAttributesPayload(selections = []) {
      const products = window.freeSampleProducts || [];
      const safeSelections = limitFreeSamplesToTwo(selections);

      const attributes = {
        // Keep free_samples null to rely on the readable attributes only
        free_samples: null,
        sample_1_name: null,
        sample_1_sku: null,
        sample_1_image: null,
        sample_2_name: null,
        sample_2_sku: null,
        sample_2_image: null
      };

      const expandedSelections = [];
      safeSelections.forEach((selection) => {
        const qty = selection.quantity || 1;
        for (let i = 0; i < qty && expandedSelections.length < 2; i += 1) {
          expandedSelections.push(selection);
        }
      });

      expandedSelections.slice(0, 2).forEach((selection, index) => {
        const productData = products.find(product => product.variant_id === selection.variantId || product.id === selection.variantId) || {};
        const slot = index + 1;

        attributes[`sample_${slot}_name`] = selection.title || productData.title || '';
        attributes[`sample_${slot}_sku`] = selection.sku || productData.variant_sku || '';
        attributes[`sample_${slot}_image`] = selection.image || productData.image || '';
      });

      return attributes;
    },

    async syncFreeSampleAttributes(selections) {
      const payload = this.buildSampleAttributesPayload(selections);
      await this.updateCartAttributes(payload);
    },

    async updateCartAttributes(attributes) {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ attributes: attributes })
        });
        
        const updatedCart = await response.json();
        this.$store.cart.cart = updatedCart;
        this.$store.cart.items = updatedCart.items;
      } catch (error) {
        console.error('Error updating cart attributes:', error);
      }
    },

    async addToCart(variantId, properties) {
      this.loading = true;

      let cartItems = [{
        id: variantId,
        quantity: 1
      }];

      if (properties) {
        cartItems[0].properties = properties;
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: cartItems
          })
        });

        if (!response.ok) {
          throw new Error(`Cart add failed with status ${response.status}`);
        }

        await response.json();
        await this.fetchCart();
      } catch (error) {
        console.error('Error adding free gift:', error);
      } finally {
        this.loading = false;
      }
    }
  }));
  

  function initCartSlider(container, paddingRight){
    const paddingRightMobile = paddingRight / 2
    new Splide('#splide-' + container, {
      perPage: 1,
      perMove: 1,
      pagination: false,
      arrows: true,
      rewind: false,
      gap: '.9rem',
      padding    : {
        right   : paddingRight + 'rem',
        left    : '1rem',
      },
      breakpoints: {
        1024: {
          perPage: 1,
          gap: '1.13rem',
        },
        640: {
          perPage: 1,
          padding    : {
            right   : paddingRightMobile + 'rem',
            left    : '1rem',
          },
          gap: '1.12rem',
        },
      }
    }).mount();
  }

  Alpine.data('productRecommendations', () => ({
    recommendations: [],
    products: window.recommendedProducts || [],
    init() {
      //this.fetchRecommendations();

      this.$nextTick(() => {
        initCartSlider('recommendations', 8);
      });
    },

    fetchRecommendations() {
      // Get the first product from the cart store
      if (this.$store.cart.cart.items.length === 0) {
        return;
      }

      const firstProduct = this.$store.cart.cart.items[0];

      fetch('/recommendations/products?section_id=recommendations&product_id=' + firstProduct.product_id+ '&limit=4')
        .then(response => response.text())
        .then(async (responseHTML) => {
          //this.recommendations = data.products;
          //console.log('Data', data);
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = responseHTML;
          const recommendationsContainer = tempDiv.querySelector('#shopify-section-recommendations');
          if (recommendationsContainer) {
            document.querySelector('#product-recommendation-container').innerHTML = recommendationsContainer.innerHTML;

            this.$nextTick(() => {
              initCartSlider('recommendations', 8);
            });
          }

        })
        .catch(error => console.error('Error fetching recommendations:', error));
    },
    addToCart(id){
      this.loading = true;

      let cartItems = [{
        id: id,
        quantity: 1
      }];

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cartItems
        })
      })
      .then(res => res.json())
      .then(() => {
        this.fetchCart();
      })
      .finally(() => {
        this.loading = false;
        // Emit cart:refresh
        window.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(error => console.error('Error adding free gift:', error));
    }
  }));

  Alpine.data('freeSampleProducts', () => ({
    products: window.freeSampleProducts,

    init(){
      this.$nextTick(() => {
        initCartSlider('freesamples', 14);
      });
    },

    get parsedSelections(){
      const attributes = this.$store.cart.cart?.attributes || {};
      const products = window.freeSampleProducts || [];
      return parseFreeSampleAttribute(attributes, products);
    },

    get selectedSamples(){
      return this.parsedSelections.map((selection) => selection.variantId);
    },

    get freeSamplesInCart(){
      return this.parsedSelections.reduce((total, selection) => total + (selection.quantity || 1), 0);
    },

    hasSample(variantId){
      const id = parseInt(variantId, 10);
      return this.selectedSamples.includes(id);
    },

    addSample(id){
      const variantId = parseInt(id, 10);
      if (!variantId || this.freeSamplesInCart >= 2) {
        return;
      }

      this.$dispatch('cart:add-free-sample', { variantId });
    }
  }));
});

function initGWPSlider(){
    const gwpSlider = document.querySelector('#splide-gwp');
    if (gwpSlider) {
      new Splide('#splide-gwp', {
        perPage: 2,
        perMove: 2,
        pagination: false,
        arrows: false,
        gap: '0.5rem'
      }).mount();
    }
}

// DOM loaded
document.addEventListener('DOMContentLoaded', () => {
  // Check if there are any URL parameters indicating cart should be opened
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('open_cart') === 'true') {
    window.dispatchEvent(new CustomEvent('cart:open'));
  }

  initGWPSlider();
});